import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import * as yup from 'yup'
import Input from 'components/Input/Input'
import Modal from 'components/Modal/Modal'
import { RequiredLabel } from 'components/RequiredLabel/RequiredLabel'
import { Formik, Form, Field } from 'formik'
import { SelectDropdown } from 'components/SelectDropdown/SelectDropdown'
import theme from 'lib/theme'
import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { getCountries, isValidCoordinate } from 'lib/utils'
import { Switch } from 'components/Switch/Switch'
import {
  usePostCollectionPoint,
  usePostDeliveryMethod,
} from 'hooks/useServiceAreas'
import { Country } from 'types/model/common'
import { useAlert } from 'providers/AlertProvider'
import { useServiceAreas } from 'providers/ServiceAreasProvider'
import { createDeliveryMethodPayload } from './utils'

const initialValues = {
  name: '',
  description: '',
  street: '',
  building: '',
  postcode: '',
  city: '',
  country: '',
  latitude: '',
  longitude: '',
  supportEmail: '',
  supportPhone: '',
  active: false,
  deliveryType: null,
}

interface CollectionPointFormProps {
  name: string
  description: string
  street: string
  postcode: string
  building: string
  city: string
  country: string
  supportPhone: string
  supportEmail: string
  latitude: string
  longitude: string
  active: boolean
  deliveryType: { value: number; label: string } | null
}

type NewGroupModalProps = {
  placeId: string
  setModal: Dispatch<SetStateAction<{ right: boolean }>>
  modal: { right: boolean }
  countries: Country[]
}

export const NewCollectionPointModal = ({
  placeId,
  setModal,
  modal,
  countries,
}: NewGroupModalProps) => {
  const { t } = useTranslation()
  const { setAlert } = useAlert()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { currencyCode } = useServiceAreas()

  const { mutateAsync, isLoading: isLoadingPostRequest } =
    usePostCollectionPoint(placeId as string)
  const { mutateAsync: createDeliveryMethod } = usePostDeliveryMethod(
    placeId as string
  )

  const createPickupDeliveryMethod = async (
    id: string,
    name: string,
    resetForm: () => void
  ) => {
    const payload = createDeliveryMethodPayload(
      'pickup',
      currencyCode,
      name,
      id
    )

    await createDeliveryMethod(payload, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
        setModal({ right: false })
        resetForm()
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'right',
        })
      },
    })
  }

  const handleSubmit = async (
    values: CollectionPointFormProps,
    resetForm: () => void
  ) => {
    const country = values.country
      ? countries.filter(
          (country: Country) => country.code === values.country
        )[0].code
      : ''
    const latitude = values.latitude ? parseFloat(values.latitude) : undefined
    const longitude = values.longitude
      ? parseFloat(values.longitude)
      : undefined
    const modifiedValues = {
      name: values.name,
      description: values.description,
      street: values.street,
      building: values.building,
      postcode: values.postcode,
      city: values.city,
      country,
      latitude,
      longitude,
      support_email: values.supportEmail,
      support_tel: values.supportPhone,
      active_for_ordering: values.active,
      ...(values.deliveryType?.value && {
        delivery_type: values.deliveryType.value,
      }),
    }
    await mutateAsync(modifiedValues, {
      onSuccess: (data) => {
        createPickupDeliveryMethod(
          data?.data?.data?.id.toString() ?? '',
          values.name,
          resetForm
        )
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'right',
        })
      },
    })
  }

  const validationSchema = yup.object({
    name: yup.string().required(t('required')),
    description: yup.string(),
    street: yup.string().min(2, t('atLeastTwoCharacters')),
    building: yup.string(),
    city: yup.string().min(2, t('atLeastTwoCharacters')),
    postcode: yup.string(),
    country: yup.string(),
    latitude: yup
      .string()
      .required(t('required'))
      .test('isLatitude', t('validLatitude'), (num) => {
        if (num) {
          return isValidCoordinate(num as string, 'latitude')
        }
        return true
      }),
    longitude: yup
      .string()
      .required(t('required'))
      .test('isLongitude', t('validLongitude'), (num) => {
        if (num) {
          return isValidCoordinate(num as string, 'longitude')
        }
        return true
      }),
    supportEmail: yup.string(),
    supportPhone: yup.string(),
    active: yup.boolean(),
    deliveryType: yup.object({ label: yup.string(), value: yup.number() }),
  })

  const handleCloseModal = () => {
    setModal({ right: false })
  }

  const deliveryTypes = [
    { label: t('manned'), value: 1 },
    { label: t('locker'), value: 2 },
  ]

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) =>
        handleSubmit(values, resetForm)
      }>
      {({ isValid, dirty, submitForm, errors, setFieldValue, values }) => (
        <Modal
          showButton
          buttonIsDisabled={!isValid || !dirty}
          isLoadingRequest={isLoadingPostRequest}
          onButtonClick={submitForm}
          buttonText={t('save')}
          setModal={handleCloseModal}
          modal={modal}
          title={t('serviceArea.newCollectionPoint')}>
          <Form>
            <Box
              display='flex'
              flexDirection='column'
              justifyContent='flex-start'>
              <Input
                errorMessage={errors.name}
                label={<RequiredLabel inputLabel='name' />}
                name='name'
                type='text'
                variant='filled'
                fullWidth
                width='100%'
              />
              <Autocomplete
                options={deliveryTypes}
                onChange={(e, value, reason, details) => {
                  if (reason === 'selectOption') {
                    if (details) {
                      setFieldValue(
                        'deliveryType',
                        deliveryTypes?.find(
                          (deliveryType) =>
                            deliveryType.value === details.option.value
                        ) || null
                      )
                    }
                  }
                  if (reason === 'clear') {
                    setFieldValue('deliveryType', null)
                  }
                }}
                value={values.deliveryType || null}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                getOptionLabel={(option) => option.label ?? ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name='deliveryType'
                    label={t('deliveryType')}
                    InputLabelProps={{
                      shrink: true,
                      sx: { top: '-6px', left: '-4px' },
                    }}
                    variant='filled'
                    sx={{
                      '& .MuiFilledInput-root': {
                        paddingTop: '2px',
                      },
                      marginBottom: theme.spacing(4),
                    }}
                  />
                )}
                id='deliveryType'
              />
              <Input
                multiline
                rows={3}
                errorMessage={errors.description}
                label={t('serviceArea.description')}
                name='description'
                type='text'
                variant='filled'
                fullWidth
                width='100%'
              />
              <Typography
                variant='body2'
                sx={{
                  marginBottom: theme.spacing(4),
                  marginTop: theme.spacing(4),
                }}>
                {t('address')}
              </Typography>
              <Box
                display={isMobile ? 'block' : 'flex'}
                justifyContent='space-between'>
                <Input
                  errorMessage={errors.street as string | undefined}
                  label={t('street')}
                  name='street'
                  type='text'
                  variant='filled'
                  width='66%'
                />
                <Input
                  errorMessage={errors.building as string | undefined}
                  label={t('building')}
                  name='building'
                  type='text'
                  variant='filled'
                  width='30%'
                />
              </Box>
              <Box
                display={isMobile ? 'block' : 'flex'}
                justifyContent='space-between'>
                <Input
                  errorMessage={errors.postcode as string | undefined}
                  label={t('postCode')}
                  name='postcode'
                  type='text'
                  variant='filled'
                  width='30%'
                />
                <Input
                  errorMessage={errors.city as string | undefined}
                  label={t('city')}
                  name='city'
                  type='text'
                  variant='filled'
                  width='66%'
                />
              </Box>
              <Field
                type='select'
                component={SelectDropdown}
                name='country'
                selectedValue={values.country}
                selectOptions={getCountries(countries)}
                handleChange={(value: string) => {
                  setFieldValue('country', value)
                }}
                label={t('country')}
                style={{ width: '100%' }}
              />
              <Typography
                variant='body2'
                sx={{
                  marginBottom: theme.spacing(4),
                }}>
                {t('location')}
              </Typography>
              <Box
                display={isMobile ? 'block' : 'flex'}
                justifyContent='space-between'>
                <Input
                  errorMessage={errors.latitude as string | undefined}
                  label={<RequiredLabel inputLabel='latitude' />}
                  name='latitude'
                  type='text'
                  variant='filled'
                  width='48%'
                />
                <Input
                  errorMessage={errors.longitude as string | undefined}
                  label={<RequiredLabel inputLabel='longitude' />}
                  name='longitude'
                  type='text'
                  variant='filled'
                  width='48%'
                />
              </Box>
              <Typography
                variant='body2'
                sx={{
                  marginBottom: theme.spacing(4),
                  marginTop: theme.spacing(1),
                }}>
                {t('placeContact.support')}
              </Typography>
              <Box
                display={isMobile ? 'block' : 'flex'}
                justifyContent='space-between'>
                <Input
                  errorMessage={errors.supportEmail as string | undefined}
                  label={t('placeContact.supportEmail')}
                  name='supportEmail'
                  type='text'
                  variant='filled'
                  width='48%'
                />
                <Input
                  errorMessage={errors.supportPhone as string | undefined}
                  label={t('placeContact.supportPhone')}
                  name='supportPhone'
                  type='text'
                  variant='filled'
                  width='48%'
                />
              </Box>
              <Switch
                label={t('serviceArea.active')}
                value={values.active}
                name='active'
                setFieldValue={setFieldValue}
                ariaLabel={t('serviceArea.toggleActive')}
              />
            </Box>
          </Form>
        </Modal>
      )}
    </Formik>
  )
}
